import React, { FC } from "react"
import { Link } from "gatsby"

import { generateConditionalClassName } from "@utils"

import Card, { CARD_VARIANTS, CardFragment } from "@components/Card/Card"
import TextLink from "@components/TextLink/TextLink"

import "./Cards.scss"

export enum CARDS_VARIANTS {
  COMPACT = "COMPACT", // A compact style with cards laid out horizontally
  COMPACT_WITH_DESCRIPTION = "COMPACT_WITH_DESCRIPTION", // A compact style with cards laid out horizontally
  DEFAULT = "DEFAULT", // 3-up grid
  LARGE = "LARGE", // 2-up grid
  LARGE_WITH_DESCRIPTION = "LARGE_WITH_DESCRIPTION", // 2-up grid with descriptions
  SMALL = "SMALL", // 4-up grid
  WITH_FEATURED = "WITH_FEATURED", // With a large featured 1st card (displays CARD_VARIANT.FEATURE_HORIZONTAL if only 1 card is provided)
}

interface CardsProps {
  className?: string
  isAboveFold?: boolean
  items: CardFragment[]
  moreLink?: string
  moreText?: string
  onClickCard?: (cardData: CardFragment) => void
  showSubcategory?: boolean
  title?: string
  variant?: CARDS_VARIANTS
}

const Cards: FC<CardsProps> = ({
  className,
  isAboveFold,
  items,
  moreLink,
  moreText,
  onClickCard,
  showSubcategory,
  title,
  variant = CARDS_VARIANTS.DEFAULT,
}) => (
  <div className={`Cards variant--${variant}${className ? ` ${className}` : ""}`}>
    {title && (
      <div className="Cards--title">
        <h3>{title}</h3>
        {moreLink && <TextLink href={moreLink}>{moreText}</TextLink>}
      </div>
    )}
    {variant === CARDS_VARIANTS.WITH_FEATURED && (
      <div className="grid">
        {items.map((item, i) => {
          if (!item) return null
          const isFirstCard = i === 0
          const isOnlyCard = items.length === 1
          return (
            <Card
              key={item.title}
              className={generateConditionalClassName({
                column: true,
                "column--1-4-S": i === 1,
                "column--4-7-S": i === 2,
                "column--1-7-M": i === 1,
                "column--7-13-M": i === 2,
                "column--1-9-L": !isOnlyCard && i === 0,
                "column--9-13-L": i === 1 || i === 2,
              })}
              includeDescription={isFirstCard}
              isAboveFold={isAboveFold}
              item={item}
              onClick={onClickCard}
              showSubcategory={showSubcategory}
              variant={
                isOnlyCard ?
                  CARD_VARIANTS.FEATURED_HORIZONTAL :
                  isFirstCard ?
                    CARD_VARIANTS.FEATURED :
                    CARD_VARIANTS.DEFAULT
              }
            />
          )
        })}
      </div>
    )}
    {[
      CARDS_VARIANTS.COMPACT,
      CARDS_VARIANTS.COMPACT_WITH_DESCRIPTION,
      CARDS_VARIANTS.DEFAULT,
      CARDS_VARIANTS.LARGE,
      CARDS_VARIANTS.LARGE_WITH_DESCRIPTION,
      CARDS_VARIANTS.SMALL,
    ].includes(variant) && (
        <div className="flex">
          {items.map((item) => !item ? null : (
            <Card
              key={item.title}
              className={generateConditionalClassName({
                "flex-item--1": variant !== CARDS_VARIANTS.SMALL,
                "flex-item--2": variant === CARDS_VARIANTS.SMALL,
                "flex-item--2-M": [CARDS_VARIANTS.DEFAULT, CARDS_VARIANTS.LARGE, CARDS_VARIANTS.LARGE_WITH_DESCRIPTION].includes(variant),
                "flex-item--3-L": variant === CARDS_VARIANTS.DEFAULT,
                "flex-item--4-L": variant === CARDS_VARIANTS.SMALL,
              })}
              includeDescription={[CARDS_VARIANTS.COMPACT_WITH_DESCRIPTION, CARDS_VARIANTS.LARGE_WITH_DESCRIPTION].includes(variant)}
              item={item}
              onClick={onClickCard}
              showSubcategory={showSubcategory}
              variant={[CARDS_VARIANTS.COMPACT, CARDS_VARIANTS.COMPACT_WITH_DESCRIPTION].includes(variant) ? CARD_VARIANTS.HORIZONTAL : CARD_VARIANTS.DEFAULT}
            />
          ))}
        </div>
      )}
    {moreLink && (
      <Link to={moreLink} className="more-link">{moreText}</Link>
    )}
  </div>
)

export default Cards
